import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Calendar, Clock, MapPin, Tag, ArrowRight, ChevronLeft, Share2 } from "lucide-react";
import { api } from "../lib/api";

// Minimal markdown → HTML renderer (h2, h3, lists, bold, links, paragraphs)
function renderMarkdown(md) {
  const lines = md.split("\n");
  const out = [];
  let inList = false;
  for (let raw of lines) {
    let line = raw;
    // links [text](url)
    line = line.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-fegreen font-semibold underline">$1</a>');
    // bold
    line = line.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");

    if (line.startsWith("## ")) {
      if (inList) { out.push("</ul>"); inList = false; }
      out.push(`<h2 class="text-2xl lg:text-3xl font-bold text-fegray-dark mt-10 mb-4">${line.slice(3)}</h2>`);
    } else if (line.startsWith("### ")) {
      if (inList) { out.push("</ul>"); inList = false; }
      out.push(`<h3 class="text-xl font-bold text-fegray-dark mt-6 mb-3">${line.slice(4)}</h3>`);
    } else if (line.startsWith("- ")) {
      if (!inList) { out.push('<ul class="space-y-2 my-4 list-disc list-inside text-fegray">'); inList = true; }
      out.push(`<li>${line.slice(2)}</li>`);
    } else if (line.trim() === "") {
      if (inList) { out.push("</ul>"); inList = false; }
      out.push("");
    } else {
      if (inList) { out.push("</ul>"); inList = false; }
      out.push(`<p class="text-fegray leading-relaxed mb-4">${line}</p>`);
    }
  }
  if (inList) out.push("</ul>");
  return out.join("\n");
}

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [other, setOther] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setNotFound(false);
    api.get(`/blog/${slug}`).then((r) => setPost(r.data)).catch(() => setNotFound(true));
    api.get("/blog").then((r) => setOther(r.data));
  }, [slug]);

  // SEO meta — update document title/keywords
  useEffect(() => {
    if (post) {
      document.title = `${post.title} — FORM'elec`;
      const setMeta = (name, content) => {
        let el = document.querySelector(`meta[name="${name}"]`);
        if (!el) {
          el = document.createElement("meta");
          el.setAttribute("name", name);
          document.head.appendChild(el);
        }
        el.setAttribute("content", content);
      };
      setMeta("description", post.excerpt);
      setMeta("keywords", (post.keywords || []).join(", "));
    }
    return () => { document.title = "FORM'elec"; };
  }, [post]);

  if (notFound) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-6 text-center">
        <div>
          <h1 className="text-3xl font-bold text-fegray-dark mb-3">Article introuvable</h1>
          <Link to="/blog" className="btn-green mt-4">Retour au blog</Link>
        </div>
      </div>
    );
  }
  if (!post) {
    return <div className="min-h-[40vh] flex items-center justify-center text-fegray">Chargement…</div>;
  }

  const related = other.filter((p) => p.slug !== post.slug).slice(0, 3);

  return (
    <div className="bg-white">
      {/* Hero */}
      <section className="bg-fegray-light pt-10 lg:pt-12 pb-12 px-6 lg:px-10">
        <div className="max-w-4xl mx-auto">
          <Link to="/blog" className="inline-flex items-center gap-2 text-fegreen font-semibold text-sm mb-6 hover:underline">
            <ChevronLeft className="w-4 h-4" /> Tous les articles
          </Link>
          <div className="flex flex-wrap items-center gap-3 mb-4 text-xs">
            <span className="px-3 py-1.5 rounded-full bg-fegreen text-white font-bold uppercase tracking-wider">
              {post.category}
            </span>
            {post.locality && (
              <span className="flex items-center gap-1 text-fegray">
                <MapPin className="w-3.5 h-3.5" /> {post.locality}
              </span>
            )}
            <span className="flex items-center gap-1 text-fegray">
              <Calendar className="w-3.5 h-3.5" /> {new Date(post.published_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
            </span>
            <span className="flex items-center gap-1 text-fegray">
              <Clock className="w-3.5 h-3.5" /> {post.read_time} min de lecture
            </span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-fegray-dark leading-tight">
            {post.title}
          </h1>
          <p className="mt-5 text-base lg:text-lg text-fegray">{post.excerpt}</p>
        </div>
      </section>

      {/* Cover */}
      <section className="px-6 lg:px-10 -mt-6 lg:-mt-10 relative z-10">
        <div className="max-w-4xl mx-auto rounded-2xl lg:rounded-3xl overflow-hidden shadow-xl">
          <img src={post.cover} alt={post.title} className="w-full aspect-[16/9] object-cover" />
        </div>
      </section>

      {/* Body */}
      <section className="py-12 lg:py-16 px-6 lg:px-10">
        <div className="max-w-3xl mx-auto">
          <article
            data-testid="blog-article-body"
            className="prose-fe"
            dangerouslySetInnerHTML={{ __html: renderMarkdown(post.content) }}
          />

          {/* Keywords */}
          {post.keywords?.length > 0 && (
            <div className="mt-12 pt-8 border-t border-gray-100">
              <div className="flex items-center gap-2 mb-3 text-xs uppercase tracking-wider font-bold text-fegreen">
                <Tag className="w-3.5 h-3.5" /> Mots-clés
              </div>
              <div className="flex flex-wrap gap-2">
                {post.keywords.map((k) => (
                  <span key={k} className="px-3 py-1 rounded-full bg-fegray-light text-fegray text-xs font-medium">
                    {k}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="mt-12 bg-fegreen-light rounded-2xl p-8 lg:p-10 text-center">
            <h3 className="text-2xl font-bold text-fegray-dark mb-3">
              Prête à essayer une séance ?
            </h3>
            <p className="text-fegray mb-6 max-w-xl mx-auto">
              Première séance découverte à domicile. Bilan personnalisé.
              Crédit d&apos;impôt 50% applicable dès la première facture.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Link to="/reservation" className="btn-green">
                Réserver ma séance <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/contact" className="btn-outline-green">
                Me contacter
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="bg-fegray-light py-16 px-6 lg:px-10">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl lg:text-3xl font-bold text-fegray-dark mb-8 text-center">
              Articles à découvrir aussi
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((p) => (
                <Link key={p.slug} to={`/blog/${p.slug}`} className="fe-card overflow-hidden group">
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={p.cover} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-5">
                    <div className="text-[10px] uppercase tracking-wider text-fegreen font-bold mb-2">{p.category}</div>
                    <h3 className="text-base font-bold text-fegray-dark group-hover:text-fegreen line-clamp-2">
                      {p.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
